<script lang="ts" setup>
import { ref, computed, watchEffect } from 'vue';
import pMap from 'p-map';

import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import useModal, { ModalTypes } from '../../composables/useModal';
import { useManageGroup } from '../../composables/useManageGroup';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';
import Modal from '../Modal.vue';
import DeleteUsersInGroupsStatus from '../GroupSidePanel/DeleteUsersInGroupsStatus.vue';

const { t } = useLocale();
const { toast } = useToast();
const { organization } = useCurrentOrganization();
const { deleteGroup, deleteGroupWithStudents } = useManageGroup();

const props = defineProps<{
  name: ModalTypes;
  groupUids: string[];
}>();
const { isOpen, close } = useModal(props.name);
const emits = defineEmits<{
  (event: 'groupsDeleted', value: string[]): void;
}>();

const isDeleting = ref(false);
const deleteStudents = ref(false);

// step 1: Confirm deleting the groups, optionally deleting the student accounts too,
//  step 2: Confirm deleting the student accounts, only reached when that was opted into

enum Step {
  CONFIRM_DELETION = 'confirmDeletion',
  CONFIRM_STUDENT_DELETION = 'confirmStudentDeletion',
}
const step = ref<Step>(Step.CONFIRM_DELETION);
const setStep = (newStep: Step) => {
  step.value = newStep;
};
const isStep = (checkStep: Step) => {
  return step.value === checkStep;
};

const deleteUsersInGroupsStatus = ref<InstanceType<typeof DeleteUsersInGroupsStatus> | null>(null);

const reset = () => {
  isDeleting.value = false;
  deleteStudents.value = false;
  setStep(Step.CONFIRM_DELETION);
};

watchEffect(() => {
  if (isOpen.value) {
    reset();
  }
});

const isCheckingUsers = computed(() => deleteUsersInGroupsStatus.value?.isPreparing === true);
const hasUsersToDelete = computed(() => deleteUsersInGroupsStatus.value?.hasUsersToDelete === true);
const hasNoUsersToDelete = computed(
  () => deleteUsersInGroupsStatus.value?.hasCheckCompleted === true && !hasUsersToDelete.value,
);

const onDeleteGroup = async (groupUid: string) => {
  if (!organization.value) {
    return;
  }

  if (deleteStudents.value === true) {
    await deleteGroupWithStudents(groupUid, organization.value.uid);
  }

  if (deleteStudents.value === false) {
    await deleteGroup(groupUid, organization.value.uid);
  }

  return;
};

const deleteSelectedGroups = async () => {
  const groupUids = [...props.groupUids];
  isDeleting.value = true;

  await pMap(groupUids, onDeleteGroup, { concurrency: 5 }).catch(() => {
    toast(t('lcp.error_deleting_groups'), 'error');
  });

  isDeleting.value = false;
  close();
  toast(t('lcp.groups_deleted_count', { count: groupUids.length }), 'success');
  emits('groupsDeleted', groupUids);
};

// Deleting the student accounts as well is destructive enough to be worth confirming on its own.
const confirmStudentDeletion = async () => {
  if (deleteStudents.value && hasUsersToDelete.value) {
    setStep(Step.CONFIRM_STUDENT_DELETION);
    return;
  }

  await deleteSelectedGroups();
};

const action = computed(() => {
  if (isStep(Step.CONFIRM_STUDENT_DELETION)) {
    return deleteSelectedGroups;
  }

  return confirmStudentDeletion;
});
const actionLabel = computed(() => {
  if (isStep(Step.CONFIRM_STUDENT_DELETION)) {
    return t('lcp.confirm_delete');
  }

  return t('lcp.delete_groups');
});
const cancelAction = computed(() => {
  if (isStep(Step.CONFIRM_STUDENT_DELETION)) {
    return () => setStep(Step.CONFIRM_DELETION);
  }

  return close;
});
</script>

<template>
  <Modal
    :name="name"
    :title="t('lcp.confirm_multiple_groups_deletion')"
    :is-open="isOpen"
    :show-close-btn="true"
    :action="action"
    :action-label="actionLabel"
    :action-disabled="isDeleting"
    action-color="error"
    :loading="isDeleting || deleteUsersInGroupsStatus?.isPreparing"
    :cancel-action="cancelAction"
    :cancel-action-label="t('lcp.back')"
    cancel-action-color="success"
    @close="reset"
  >
    <v-row no-gutters class="ga-4">
      <template v-if="step === Step.CONFIRM_DELETION">
        <v-col cols="12">
          <p>
            {{ t('lcp.confirm_multiple_groups_deletion_message', { count: groupUids.length }) }}
          </p>
        </v-col>
        <v-col cols="12">
          <v-checkbox
            v-model="deleteStudents"
            :label="t('lcp.delete_students_in_groups')"
            color="primary"
            :disabled="isCheckingUsers || hasNoUsersToDelete"
            hide-details
          />
          <p v-if="hasNoUsersToDelete" class="pt-2 text-caption">
            {{ t('lcp.no_students_to_delete_in_groups') }}
          </p>
        </v-col>
        <v-col cols="12">
          <p v-if="!deleteStudents">
            {{ t('lcp.confirm_multiple_groups_deletion_no_delete_students_message', { count: groupUids.length }) }}
          </p>
          <p v-else>
            {{ t('lcp.confirm_multiple_groups_deletion_yes_delete_students_message', { count: groupUids.length }) }}
          </p>
        </v-col>
      </template>
      <v-col v-if="step === Step.CONFIRM_STUDENT_DELETION" cols="12">
        <p>
          {{ t('lcp.confirm_multiple_groups_deletion_final_accounts_message') }}
        </p>
        <p class="pt-4">
          {{ t('lcp.confirm_group_deletion_final_data_message') }}
        </p>
        <p class="pt-4">
          {{ t('lcp.confirm_group_deletion_final_undone_message') }}
        </p>
      </v-col>
      <v-col v-show="deleteStudents">
        <DeleteUsersInGroupsStatus ref="deleteUsersInGroupsStatus" :visible="isOpen" :selected-group-uids="groupUids" />
      </v-col>
    </v-row>
  </Modal>
</template>

<style scoped lang="scss"></style>
